// Панели «Структура» (дерево разделов с живыми номерами, перетаскивание разделов вместе с содержимым,
// свёртывание, счётчики рисунков/таблиц; навигация по объектам) и «Проверка» (полная проверка sto_check,
// сгруппированная по разделам СТО, исправления, «пропустить»; подсказки при наборе), сводка перед сдачей.
import { useEffect, useMemo, useState } from 'react';
import type { Ctx, DialogProps, LintIssue } from '../../core/registry';
import type { Finding } from '../../core/types';
import type { NumInfo } from '../../core/numbering';
import { useApp } from '../../core/store';
import { useNumbering } from '../../core/editor/numberingPlugin';
import { gotoNode } from '../../core/docIndex';
import { runCheck, runLint } from '../../core/project';
import { Btn, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { useEditorTick } from '../../ui/hooks';
import { stoSection, sectionSortKey, wt } from './data';
import { moveSection, setHeadingLevel } from './structure';
import { findingFixes } from './checkfix';
import { batchEdits } from '../../core/editor/batch';
import { goTo } from './lint';
import { ignores } from './profile';
import { applyTypography } from './typography';
import { TargetThumb, allTargets, KIND_NAMES } from './refs';

// ------------------------------------------------------------------------------------------------ структура
interface Row { id: string; info: NumInfo; depth: number; counts: { fig: number; tbl: number; eq: number; lst: number }; children: number; top: boolean }

export function StructurePanel({ ctx }: { ctx: Ctx }) {
  const nb = useNumbering();
  const ed = useEditorTick();
  const [tab, setTab] = useState<'tree' | 'objects'>('tree');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; before: boolean } | null>(null);
  const [kind, setKind] = useState<string>('all');
  const focusClicked = useApp((s) => s.focusNode);
  // подсветка - раздел, где сейчас курсор (а не последний щелчок по панели)
  const head = ed ? ed.state.selection.head : -1;
  const curSec = useMemo(() => {
    if (!ed) return null;
    let cur: string | null = null;
    ed.state.doc.descendants((n, pos) => {
      if (pos > head) return false;
      if ((n.type.name === 'heading' || n.type.name === 'structHeading' || n.type.name === 'appendix') && n.attrs.id) cur = n.attrs.id;
      return n.type.name === 'doc' || n.type.name === 'appendix';
    });
    return cur;
  }, [ed?.state.doc, head]);
  const focus = curSec || focusClicked;

  // строки дерева + счётчики объектов (с «пузырьком» к родителям)
  const rows = useMemo(() => {
    const out: Row[] = [];
    const stack: Row[] = [];
    const top = new Set<string>();
    ed?.state.doc.forEach((n) => { if (n.attrs?.id) top.add(n.attrs.id); });
    // пустые заголовки нумерация пропускает (как writer) - в дереве они видны как «(без названия)», чтобы их
    // можно было найти и заполнить или удалить
    const empty = new Map<string, NumInfo>();
    const at = new Map<string, number>();              // id -> порядковый номер в документе
    const empties: string[] = [];
    let k = 0;
    ed?.state.doc.descendants((n) => {
      const id = n.attrs?.id;
      if (id) {
        at.set(id, k++);
        if (n.type.name === 'heading' && !n.textContent.trim()) {
          empty.set(id, { kind: 'sec', num: '', label: '', title: '', ref: '', level: Number(n.attrs.level) || 1 } as any);
          empties.push(id);
        }
      }
      return n.type.name === 'doc' || n.type.name === 'appendix';
    });
    const seq: string[] = [];
    let e = 0;
    for (const id of nb.order) {
      const i = at.get(id);
      while (i !== undefined && e < empties.length && (at.get(empties[e]) as number) < i) seq.push(empties[e++]);
      seq.push(id);                                    // псевдоузлы (раздел об ИИ) - на своём месте из нумерации
    }
    while (e < empties.length) seq.push(empties[e++]);
    for (const id of seq) {
      const info = nb.byId.get(id) || empty.get(id);
      if (!info) continue;
      if (['sec', 'struct', 'app'].includes(info.kind)) {
        const lvl = info.kind === 'sec' ? info.level || 1 : 1;
        while (stack.length && (stack[stack.length - 1].info.kind === 'sec' ? stack[stack.length - 1].info.level || 1 : 1) >= lvl) stack.pop();
        const row: Row = { id, info, depth: stack.length, counts: { fig: 0, tbl: 0, eq: 0, lst: 0 }, children: 0, top: top.has(id) };
        if (stack.length) stack[stack.length - 1].children++;
        stack.push(row);
        out.push(row);
      } else if (info.kind in { fig: 1, tbl: 1, eq: 1, lst: 1 }) {
        for (const r of stack) (r.counts as any)[info.kind]++;
      }
    }
    return out;
  }, [nb, ed?.state.doc]);

  const hidden = new Set<string>();
  {
    let hideBelow = -1;
    for (const r of rows) {
      if (hideBelow >= 0 && r.depth > hideBelow) { hidden.add(r.id); continue; }
      hideBelow = collapsed.has(r.id) ? r.depth : -1;
    }
  }
  const drop = (target: string, before: boolean) => {
    if (!drag || !ed) return;
    if (!moveSection(ed, drag, target, before)) ctx.toast('Сюда раздел перенести нельзя (приложения - только в конце, раздел - не внутрь самого себя)', 'warn');
    setDrag(null); setOver(null);
  };
  const siblingMove = (r: Row, dir: -1 | 1) => {
    if (!ed) return;
    const same = rows.filter((x) => x.top && x.depth === r.depth && (x.info.kind === 'app') === (r.info.kind === 'app'));
    const i = same.findIndex((x) => x.id === r.id);
    const t = same[i + dir];
    if (t) moveSection(ed, r.id, t.id, dir < 0);
  };

  const targets = useMemo(() => (tab === 'objects' ? allTargets(nb).filter((t) => ['fig', 'tbl', 'eq', 'lst', 'app'].includes(t.kind)) : []), [nb, tab]);
  const c = nb.counts;
  return (
    <div className="structure" data-testid="structure-panel">
      <div className="tabs tabs-small">
        <button type="button" className={'tab' + (tab === 'tree' ? ' is-active' : '')} onClick={() => setTab('tree')}>Разделы</button>
        <button type="button" className={'tab' + (tab === 'objects' ? ' is-active' : '')} onClick={() => setTab('objects')} data-testid="structure-objects">
          Объекты <span className="tab-count">{c.figures + c.tables + c.equations + c.listings}</span></button>
      </div>
      {tab === 'tree' && <>
        {!rows.length && <div className="muted pad">Добавьте заголовок раздела (Ctrl+Alt+1) или структурный элемент (вкладка «СТО»).</div>}
        <div className="outline" onDragLeave={(e) => { if (e.currentTarget === e.target) setOver(null); }}>
          {rows.filter((r) => !hidden.has(r.id)).map((r) => {
            const i = r.info;
            const draggable = r.top && r.id !== 'meta_ai';
            const cn = r.counts;
            return (
              <div key={r.id} className={'outline-row outline-' + i.kind + ' lvl-' + (i.level || 1) + (focus === r.id ? ' is-focus' : '')
                + (over?.id === r.id ? (over.before ? ' drop-before' : ' drop-after') : '') + (drag === r.id ? ' is-drag' : '')}
                style={{ paddingLeft: 4 + r.depth * 14 }} draggable={draggable} data-testid="outline-row" data-id={r.id}
                onDragStart={(e) => { setDrag(r.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('application/x-sto-section', r.id); }}
                onDragEnd={() => { setDrag(null); setOver(null); }}
                onDragOver={(e) => { if (!drag || !r.top) return; e.preventDefault(); const b = e.currentTarget.getBoundingClientRect(); setOver({ id: r.id, before: e.clientY < b.top + b.height / 2 }); }}
                onDrop={(e) => { e.preventDefault(); if (over) drop(r.id, over.before); }}
                onClick={() => (r.id === 'meta_ai' ? ctx.openDialog('sto.meta', { tab: 'ai' }) : ed && gotoNode(ed, r.id))}>
                <span className="outline-tw" onClick={(e) => { e.stopPropagation(); if (!r.children) return; const s = new Set(collapsed); if (s.has(r.id)) s.delete(r.id); else s.add(r.id); setCollapsed(s); }}>
                  {r.children ? (collapsed.has(r.id) ? '▸' : '▾') : ''}</span>
                <span className="outline-num">{i.kind === 'app' ? i.label : i.kind === 'struct' ? '' : i.num}</span>
                <span className="outline-title">{i.title || <span className="muted">без названия</span>}</span>
                <span className="outline-counts">
                  {cn.fig > 0 && <span title="рисунков"><Icon name="image" size={11} />{cn.fig}</span>}
                  {cn.tbl > 0 && <span title="таблиц"><Icon name="table" size={11} />{cn.tbl}</span>}
                  {cn.eq > 0 && <span title="формул"><Icon name="formula" size={11} />{cn.eq}</span>}
                </span>
                {draggable && (
                  <span className="outline-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" title="Выше" onClick={() => siblingMove(r, -1)}>↑</button>
                    <button type="button" title="Ниже" onClick={() => siblingMove(r, 1)}>↓</button>
                    {i.kind === 'sec' && <>
                      <button type="button" title="Повысить уровень" onClick={() => ed && setHeadingLevel(ed, r.id, -1)} disabled={(i.level || 1) <= 1}>←</button>
                      <button type="button" title="Понизить уровень" onClick={() => ed && setHeadingLevel(ed, r.id, 1)} disabled={(i.level || 1) >= 3}>→</button>
                    </>}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="outline-foot muted small">Перетащите раздел мышью - он переедет вместе с текстом, рисунками и таблицами; номера пересчитаются.
          Всего: {c.figures} рис., {c.tables} табл., {c.equations} форм., {c.listings} лист., {c.appendices} прил.</div>
      </>}
      {tab === 'objects' && <>
        <div className="chips-row">
          {['all', 'fig', 'tbl', 'eq', 'lst', 'app'].map((k) => (
            <button key={k} type="button" className={'chip' + (kind === k ? ' is-active' : '')} onClick={() => setKind(k)}>{k === 'all' ? 'Все' : KIND_NAMES[k]}</button>
          ))}
        </div>
        <div className="objects-list">
          {targets.filter((t) => kind === 'all' || t.kind === kind).map((t) => (
            <button key={t.id} type="button" className={'object-row' + (focus === t.id ? ' is-focus' : '')} onClick={() => ed && gotoNode(ed, t.id)} data-testid="object-row">
              <TargetThumb t={t} />
              <span><b>{t.info.label || t.info.ref}</b> {t.kind === 'eq' ? <code>{t.info.title.slice(0, 40)}</code> : t.info.title || <span className="muted">без названия</span>}</span>
            </button>
          ))}
          {!targets.length && <div className="muted pad">Рисунков, таблиц и формул пока нет.</div>}
        </div>
      </>}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ проверка
const SEV_ICON: Record<string, string> = { E: 'error', W: 'warn', I: 'info' };
const fKey = (f: Finding) => `srv|${f.code}|${f.node || ''}|${String(f.loc?.text || '').slice(0, 40)}`;

function ago(t?: number) {
  if (!t) return '';
  const s = Math.round((Date.now() - t) / 1000);
  return s < 60 ? 'только что' : s < 3600 ? `${Math.round(s / 60)} мин назад` : `${Math.round(s / 3600)} ч назад`;
}

export function CheckPanel({ ctx }: { ctx: Ctx }) {
  const check = useApp((s) => s.check);
  const lint = useApp((s) => s.lint);
  const pid = useApp((s) => s.projectId);
  const [showKnown, setShowKnown] = useState(false);
  const [onlyE, setOnlyE] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);
  const [view, setView] = useState<'full' | 'live'>(check.result ? 'full' : 'live');
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [ign, setIgn] = useState(() => ignores.list(pid));
  const [, tick] = useState(0);
  useEffect(() => { const t = window.setInterval(() => tick((x) => x + 1), 30000); return () => window.clearInterval(t); }, []);
  useEffect(() => { if (check.result) setView('full'); }, [check.result]);
  const res = check.result;
  const ignore = (k: string) => { const s = new Set(ign); if (s.has(k)) s.delete(k); else s.add(k); ignores.set(pid, s); setIgn(s); };
  const findings = (res?.findings || []).filter((f) => (showKnown || !f.known) && (!onlyE || f.sev === 'E') && (showIgnored || !ign.has(fKey(f))));
  const log = (res?.log || []).filter((l) => l.sev !== 'I' && (!onlyE || l.sev === 'E'));
  const groups = useMemo(() => {
    const m = new Map<string, { name: string; items: Finding[]; e: number }>();
    for (const f of findings) {
      const [k, name] = stoSection(f.rule);
      if (!m.has(k)) m.set(k, { name, items: [], e: 0 });
      const g = m.get(k)!;
      g.items.push(f);
      if (f.sev === 'E') g.e++;
    }
    return [...m.entries()].sort((a, b) => sectionSortKey(a[0]) - sectionSortKey(b[0]));
  }, [findings]);
  const liveE = lint.filter((x) => x.sev === 'E').length;
  const liveW = lint.filter((x) => x.sev === 'W').length;
  const toggle = (k: string) => { const s = new Set(closed); if (s.has(k)) s.delete(k); else s.add(k); setClosed(s); };
  const ignoredN = (res?.findings || []).filter((f) => ign.has(fKey(f))).length;
  return (
    <div className="check-panel" data-testid="check-panel-body">
      <div className="panel-tools">
        <Btn icon="check" label={check.running ? 'Проверка…' : 'Проверить'} title="Собрать DOCX и PDF в Word и проверить все требования СТО (F7)" primary
          onClick={() => runCheck(true)} disabled={check.running} testid="check-run" />
        <Btn icon="refresh" label="Быстро" title="Без Word: быстрее (≈1 с), но без PDF и оглавления" onClick={() => runCheck(false)} disabled={check.running} testid="check-fast" />
      </div>
      {check.running && <div className="muted pad"><Icon name="spinner" className="spin" /> {check.progress}</div>}
      {check.error && <div className="finding finding-E">{check.error}</div>}
      <div className="tabs tabs-small">
        <button type="button" className={'tab' + (view === 'full' ? ' is-active' : '')} onClick={() => setView('full')} data-testid="check-tab-full">
          Полная проверка {res ? <span className={'tab-count' + (res.errors ? ' bad' : '')}>{res.errors}</span> : null}</button>
        <button type="button" className={'tab' + (view === 'live' ? ' is-active' : '')} onClick={() => setView('live')} data-testid="check-tab-live">
          При наборе <span className={'tab-count' + (liveE ? ' bad' : '')}>{liveE + liveW}</span></button>
      </div>
      {view === 'full' && <>
        {!res && !check.running && <div className="muted pad">Полная проверка собирает документ так же, как при экспорте, и проверяет ~340 правил СТО.
          Нажмите «Проверить» (F7).</div>}
        {res && (
          <div className="check-summary" data-testid="check-summary">
            <span className={res.errors ? 'bad' : 'good'}><Icon name={res.errors ? 'error' : 'ok'} /> Ошибок: {res.errors}</span>
            <span><Icon name="warn" /> Предупреждений: {res.warnings}</span>
            <span className="muted">{res.word ? 'с Word' : 'без Word'} · {res.elapsed} с · {ago(check.at)}{res.pages ? ` · ${res.pages} с.` : ''}</span>
          </div>
        )}
        {res && (
          <div className="check-filters">
            <label className="check small"><input type="checkbox" checked={onlyE} onChange={(e) => setOnlyE(e.target.checked)} /> только ошибки</label>
            <label className="check small" title="Особенности сборки writer и следствия быстрой проверки без Word"><input type="checkbox" checked={showKnown} onChange={(e) => setShowKnown(e.target.checked)} /> особенности сборки</label>
            {ignoredN > 0 && <label className="check small"><input type="checkbox" checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} /> пропущенные ({ignoredN})</label>}
          </div>
        )}
        {res && !findings.length && !log.length && <div className="good pad"><Icon name="ok" /> Нарушений СТО не найдено.</div>}
        <div className="findings" data-testid="findings">
          {groups.map(([k, g]) => (
            <div key={k} className="fgroup">
              <button type="button" className="fgroup-head" onClick={() => toggle(k)} data-testid="finding-group">
                <span>{closed.has(k) ? '▸' : '▾'}</span><b>{k !== '~' ? k + ' ' : ''}{g.name}</b>
                <span className={'tab-count' + (g.e ? ' bad' : '')}>{g.items.length}</span>
              </button>
              {!closed.has(k) && <FixAllFull ctx={ctx} items={g.items} />}
              {!closed.has(k) && g.items.map((f, i) => <FindingCard key={i} ctx={ctx} f={f} ignored={ign.has(fKey(f))} onIgnore={() => ignore(fKey(f))} />)}
            </div>
          ))}
          {log.map((l, i) => (
            <div key={'l' + i} className={`finding finding-${l.sev}` + (l.node ? ' is-link' : '')} onClick={() => goTo(ctx, l.node)}>
              <div className="finding-head"><Icon name={SEV_ICON[l.sev]} size={14} /> <b>Сборка</b></div>
              <div className="finding-msg">{l.msg}</div>
            </div>
          ))}
        </div>
      </>}
      {view === 'live' && <LiveList ctx={ctx} lint={lint} />}
    </div>
  );
}

/** «Исправить все однотипные (N)»: первые исправления всех находок одного кода - одной отменой (Ctrl+Z). */
function FixAllFull({ ctx, items }: { ctx: Ctx; items: Finding[] }) {
  const byCode = new Map<string, Finding[]>();
  for (const f of items) if (!f.known) { const a = byCode.get(f.code) || []; a.push(f); byCode.set(f.code, a); }
  const rows = [...byCode.entries()].filter(([, fs]) => fs.length >= 2 && fs.some((f) => findingFixes(ctx, f).length));
  if (!rows.length) return null;
  return <div className="fix-all-row">{rows.map(([code, fs]) => (
    <button key={code} type="button" className="btn-mini btn-mini-primary" data-testid="finding-fix-all" data-code={code}
      title={`${fs[0].title || code}: ${fs.length} находок - исправить все сразу (одна отмена Ctrl+Z)`}
      onClick={() => {
        const ed = ctx.editor;
        if (!ed) return;
        let n = 0;
        batchEdits(ed, () => { for (const f of fs) { const fx = findingFixes(ctx, f)[0]; if (fx) { fx.run(ctx); n++; } } });
        ctx.toast(`Исправлено: ${n} (${fs[0].title || code}) - Ctrl+Z отменит все сразу; проверьте ещё раз (F7)`, 'ok', 6000);
      }}>Исправить все однотипные: {fs[0].title || code} ({fs.length})</button>
  ))}</div>;
}

/** То же для замечаний при наборе: пока есть замечания этого кода с исправлением - применяем (одна отмена). */
function fixAllLive(ctx: Ctx, code: string): number {
  const ed = ctx.editor;
  if (!ed) return 0;
  let n = 0;
  batchEdits(ed, () => {
    for (let guard = 0; guard < 300; guard++) {
      runLint();
      const fresh = (ctx.store.getState().lint as LintIssue[]).filter((l) => l.code === code && l.fix);
      if (!fresh.length) break;
      const before = ed.state.doc;
      fresh[0].fix!.run(ctx);
      if (ed.state.doc === before) break;          // исправление ничего не изменило - дальше не пытаемся
      n++;
    }
  });
  return n;
}

function FindingCard({ ctx, f, ignored, onIgnore }: { ctx: Ctx; f: Finding; ignored: boolean; onIgnore: () => void }) {
  const fixes = findingFixes(ctx, f);
  return (
    <div className={`finding finding-${f.sev}` + (f.node ? ' is-link' : '') + (f.known ? ' is-known' : '') + (ignored ? ' is-ignored' : '')}
      onClick={() => goTo(ctx, f.node)} data-testid="finding" data-code={f.code}>
      <div className="finding-head"><Icon name={SEV_ICON[f.sev]} size={14} /> <b>{f.title || f.code}</b> <span className="muted">п. {f.rule}</span>
        {f.known && <span className="badge-muted">{f.known === 'noword' ? 'без Word' : 'сборка'}</span>}</div>
      <div className="finding-msg">{f.msg}</div>
      {f.loc?.text && <div className="finding-text">«{String(f.loc.text).slice(0, 90)}»{f.loc.page ? ` · стр. ${f.loc.page}` : ''}</div>}
      {f.fix && <div className="finding-fix">Как исправить: {f.fix}</div>}
      <div className="finding-actions" onClick={(e) => e.stopPropagation()}>
        {fixes.map((x, i) => <button key={i} type="button" className={'btn-mini' + (i === 0 ? ' btn-mini-primary' : '')} onClick={() => { x.run(ctx); ctx.toast('Исправлено - проверьте ещё раз (F7)', 'ok'); }}
          data-testid="finding-fix">{x.label}</button>)}
        <button type="button" className="btn-mini" onClick={onIgnore} data-testid="finding-ignore">{ignored ? 'Вернуть' : 'Пропустить'}</button>
      </div>
    </div>
  );
}

function LiveList({ ctx, lint }: { ctx: Ctx; lint: LintIssue[] }) {
  const [sevs, setSevs] = useState<Set<string>>(new Set(['E', 'W', 'I']));
  // щелчок по виду - «только этот вид», повторный щелчок - снова все
  const pick = (s: string) => setSevs((cur) => (cur.size === 1 && cur.has(s) ? new Set(['E', 'W', 'I']) : new Set([s])));
  const sameCode = new Map<string, number>();
  for (const l of lint) if (l.code && l.fix) sameCode.set(l.code, (sameCode.get(l.code) || 0) + 1);
  const shown = lint.filter((l) => sevs.has(l.sev));
  const groups = new Map<string, LintIssue[]>();
  for (const l of shown) {
    const [k, name] = stoSection(l.rule);
    const key = k + '|' + name;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(l);
  }
  const SEV_RANK: Record<string, number> = { E: 0, W: 1, I: 2 };
  groups.forEach((arr) => arr.sort((a, b) => SEV_RANK[a.sev] - SEV_RANK[b.sev]));
  const typoN = lint.filter((l) => l.rule === '4.1' && /типограф|кавыч|тире|пробел|Диапазон/i.test(l.msg)).length;
  return (
    <div className="findings" data-testid="lint">
      <div className="sev-toggles">
        {(['E', 'W', 'I'] as const).map((s) => (
          <button key={s} type="button" className={'sev-toggle sev-toggle-' + s + (sevs.has(s) ? ' is-on' : '')} aria-pressed={sevs.has(s)}
            onClick={() => pick(s)} title={sevs.size === 1 && sevs.has(s) ? 'Показать все' : 'Показать только этот вид'}>
            <Icon name={SEV_ICON[s]} size={12} /> {{ E: 'Ошибки', W: 'Замечания', I: 'Советы' }[s]} {lint.filter((l) => l.sev === s).length}</button>
        ))}
      </div>
      {typoN > 0 && <button type="button" className="btn-mini typo-all" onClick={() => { if (ctx.editor) ctx.toast(`Исправлено мест: ${applyTypography(ctx.editor)}`, 'ok'); }}
        data-testid="typo-all">Исправить типографику во всём документе ({typoN})</button>}
      {!lint.length && <div className="good pad"><Icon name="ok" /> При наборе замечаний нет.</div>}
      {[...groups.entries()].sort((a, b) => sectionSortKey(a[0].split('|')[0]) - sectionSortKey(b[0].split('|')[0])).map(([key, items]) => (
        <div key={key} className="fgroup">
          <div className="fgroup-head is-static"><b>{key.split('|')[0] !== '~' ? key.split('|')[0] + ' ' : ''}{key.split('|')[1]}</b><span className="tab-count">{items.length}</span></div>
          {items.map((l, i) => (
            <div key={i} className={`finding finding-${l.sev}` + (l.node ? ' is-link' : '')} onClick={() => goTo(ctx, l.node)} data-testid="lint-item">
              <div className="finding-msg"><Icon name={SEV_ICON[l.sev]} size={14} /> {l.msg}{l.rule ? <span className="muted"> (п. {l.rule})</span> : null}</div>
              {l.fix && <button type="button" className="btn-mini btn-mini-primary" onClick={(e) => { e.stopPropagation(); l.fix!.run(ctx); }} data-testid="lint-item-fix">{l.fix.label}</button>}
              {l.fix && l.code && (sameCode.get(l.code) || 0) >= 2 && items.findIndex((x) => x.code === l.code) === i &&
                <button type="button" className="btn-mini" data-testid="lint-fix-all" data-code={l.code}
                  onClick={(e) => { e.stopPropagation(); const n = fixAllLive(ctx, l.code!); ctx.toast(`Исправлено: ${n} - Ctrl+Z отменит все сразу`, 'ok', 5000); }}>
                  Исправить все такие ({sameCode.get(l.code)})</button>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ перед сдачей
export function PreExportDialog({ ctx, close }: DialogProps) {
  const lint = useApp((s) => s.lint);
  const check = useApp((s) => s.check);
  const m = useApp((s) => s.meta);
  const nb = useNumbering();
  if (!m) return null;
  const e = lint.filter((x) => x.sev === 'E');
  const w = lint.filter((x) => x.sev === 'W');
  const kw = m.keywords.length;
  const sheet = m.type === 'sheet';                     // чистый лист: без титула и данных работы
  const items: [boolean, string][] = [
    ...(sheet ? [] : [
      [!!m.title && !!m.teacher.name && (!!m.discipline || m.type === 'practice'), 'Титул заполнен (тема, дисциплина, руководитель)'],
      [kw >= 5 && kw <= 15, `Ключевых слов: ${kw} (нужно 5–15)`]] as [boolean, string][]),
    [!lint.some((x) => /нет ссылки в тексте/.test(x.msg) && x.node && !x.node.startsWith('src:')), 'На все рисунки, таблицы и приложения есть ссылки'],
    [!lint.some((x) => x.node?.startsWith('src:') && /нет ссылки/.test(x.msg)), `На все источники (${nb.sourceList.length}) есть ссылки`],
    [!lint.some((x) => x.rule === '2.2 (табл. 1)' && x.sev === 'W'), `Состав работы по табл. 1 СТО («${wt(m.type).name}»)`],
    [!e.length, `Ошибок при наборе нет${e.length ? ` (сейчас ${e.length})` : ''}`],
    [!!check.result && !check.result.errors, check.result ? `Полная проверка: ошибок ${check.result.errors}` : 'Полная проверка ещё не запускалась'],
  ];
  const ready = items.every((x) => x[0]);
  const exp = (cmd: string) => { close(cmd); ctx.run(cmd); };
  return (
    <Modal title="Готовность к сдаче" onClose={() => close()} width={640} testid="preexport-dialog" footer={<>
      <Btn icon="check" label="Проверить (F7)" onClick={() => { void runCheck(true); }} disabled={check.running} />
      <span className="topbar-spacer" />
      <Btn icon="docx" label="Экспорт DOCX" onClick={() => exp('file.exportDocx')} testid="preexport-docx" />
      <Btn icon="pdf" label="Экспорт PDF" primary={ready} onClick={() => exp('file.exportPdf')} testid="preexport-pdf" />
    </>}>
      <div className={'ready-banner ' + (ready ? 'good' : 'bad')}><Icon name={ready ? 'ok' : 'warn'} size={20} />
        {ready ? 'Отчёт готов к сдаче: оформление по СТО проверено.' : 'Есть что поправить перед сдачей - список ниже.'}</div>
      <ul className="ready-list" data-testid="preexport-list">
        {items.map(([ok, t], i) => <li key={i} className={ok ? 'good' : 'bad'}><Icon name={ok ? 'ok' : 'warn'} size={14} /> {t}</li>)}
      </ul>
      {check.running && <div className="muted"><Icon name="spinner" className="spin" /> {check.progress}</div>}
      {(e.length > 0 || w.length > 0) && <div className="muted small">При наборе: {e.length} ошибок, {w.length} замечаний - подробности в панели «Проверка».</div>}
    </Modal>
  );
}

